"use client";

import { declineBraveheartChallenge } from "@/actions/match";
import {
  MatchResultModal,
  MatchScheduleModal,
  WalkoverConfirmModal,
} from "@/components/notifications";
import {
  useTournamentMatches,
  useTournamentTeams,
  useUserTeamForTournament,
} from "@/hooks";
import { useCurrentTime } from "@/hooks/useCurrentTime";
import { logEvent } from "@/lib/analytics";
import { playChickenSound } from "@/lib/utils";
import { ChallengeType, MatchStatus } from "@/types";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ActionBar, type ActionBarState } from "./ActionBar";

interface MatchActionBarProps {
  tournamentId: string;
}

/**
 * Tournament-scoped action bar that surfaces the user's most-urgent match
 * obligation: schedule a date, reschedule, or enter the result. Walkover is
 * available on every state; Braveheart decline appears only on a Braveheart
 * challenge that hasn't auto-accepted yet.
 *
 * Visual presentation lives in `ActionBar`; this component computes the
 * state, hosts the schedule/result/walkover modals, and runs the Braveheart
 * decline mutation.
 */
export function MatchActionBar({ tournamentId }: MatchActionBarProps) {
  const { data: userTeam } = useUserTeamForTournament(tournamentId);
  const { data: matches = [] } = useTournamentMatches(tournamentId);
  const { data: teams = [] } = useTournamentTeams(tournamentId);

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [isWalkoverModalOpen, setIsWalkoverModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [matchTimePassedByTimer, setMatchTimePassedByTimer] = useState(false);

  // The user's active match — CHALLENGE means "schedule needed", SCHEDULED
  // either "upcoming" or "result needed" depending on whether match time has
  // passed.
  const activeMatch = useMemo(() => {
    if (!userTeam) return null;
    return (
      matches.find(
        (m) =>
          (m.team1Id === userTeam.id || m.team2Id === userTeam.id) &&
          (m.status === MatchStatus.CHALLENGE ||
            m.status === MatchStatus.SCHEDULED),
      ) || null
    );
  }, [matches, userTeam]);

  const matchDate = useMemo(() => {
    if (!activeMatch?.matchDate) return null;
    return new Date(activeMatch.matchDate);
  }, [activeMatch?.matchDate]);

  // Timer auto-transitions us from `scheduled` to `result-needed` when the
  // match time arrives while the user is on the page.
  useEffect(() => {
    if (!matchDate) {
      setMatchTimePassedByTimer(false);
      return;
    }
    const now = new Date();
    if (matchDate <= now) {
      setMatchTimePassedByTimer(true);
      return;
    }
    setMatchTimePassedByTimer(false);
    const timer = setTimeout(
      () => setMatchTimePassedByTimer(true),
      matchDate.getTime() - now.getTime(),
    );
    return () => clearTimeout(timer);
  }, [matchDate]);

  const matchTimePassed =
    matchTimePassedByTimer || (matchDate != null && matchDate <= new Date());

  // Braveheart-decline window: a 2-team Braveheart challenge can be declined
  // by the challenged team until `braveheartResponseDeadline` passes, after
  // which it auto-accepts. We surface the decline button only inside that
  // window.
  const isScheduleAction = activeMatch?.status === MatchStatus.CHALLENGE;
  const isResultAction = activeMatch?.status === MatchStatus.SCHEDULED;
  const isBraveheartChallenge =
    activeMatch?.challengeType === ChallengeType.BRAVEHEART;
  const isChallengedTeam = userTeam?.id === activeMatch?.team2Id;

  const now = useCurrentTime();
  const braveheartResponseDeadline = activeMatch?.braveheartResponseDeadline
    ? new Date(activeMatch.braveheartResponseDeadline)
    : null;
  const braveheartResponseExpired = braveheartResponseDeadline
    ? now > braveheartResponseDeadline.getTime()
    : false;
  const canDeclineBraveheart =
    isScheduleAction &&
    isBraveheartChallenge &&
    isChallengedTeam &&
    !braveheartResponseExpired;

  // Team names — read from the reactive teams query so a rename mid-match
  // propagates without a page reload.
  const teamNames = useMemo(() => {
    if (!activeMatch || !userTeam)
      return { team1: "", team2: "", opponent: "" };
    const team1Name =
      teams.find((t) => t.id === activeMatch.team1Id)?.name ?? "";
    const team2Name =
      teams.find((t) => t.id === activeMatch.team2Id)?.name ?? "";
    const opponentTeamId =
      activeMatch.team1Id === userTeam.id
        ? activeMatch.team2Id
        : activeMatch.team1Id;
    const opponentName =
      teams.find((t) => t.id === opponentTeamId)?.name ?? "";
    return { team1: team1Name, team2: team2Name, opponent: opponentName };
  }, [activeMatch, userTeam, teams]);

  const handleDeclineBraveheart = useCallback(async () => {
    if (isProcessing || !activeMatch || !userTeam) return;
    setIsProcessing(true);
    try {
      const result = await declineBraveheartChallenge({
        matchId: activeMatch.id,
        decliningTeamId: userTeam.id,
      });
      if (result.success) {
        playChickenSound();
        logEvent("braveheart_declined", {
          match_id: activeMatch.id,
          team_id: userTeam.id,
        });
      }
    } catch (error) {
      console.error("Failed to decline challenge:", error);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, activeMatch, userTeam]);

  const handleModalSuccess = useCallback(() => {
    setIsScheduleModalOpen(false);
    setIsResultModalOpen(false);
    setIsWalkoverModalOpen(false);
  }, []);

  // Derive the visible state. `null` hides the bar entirely.
  const state = useMemo<ActionBarState | null>(() => {
    if (!activeMatch || !userTeam) return null;

    if (isScheduleAction) {
      return {
        kind: "schedule-needed",
        opponentTeamName: teamNames.opponent,
        deadline: new Date(activeMatch.challengeExpiresAt),
        canDeclineBraveheart,
      };
    }

    if (isResultAction && matchDate) {
      if (matchTimePassed) {
        return {
          kind: "result-needed",
          opponentTeamName: teamNames.opponent,
        };
      }
      return {
        kind: "scheduled",
        opponentTeamName: teamNames.opponent,
        scheduledAt: matchDate,
      };
    }

    return null;
  }, [
    activeMatch,
    userTeam,
    isScheduleAction,
    isResultAction,
    matchDate,
    matchTimePassed,
    teamNames.opponent,
    canDeclineBraveheart,
  ]);

  if (!state || !activeMatch || !userTeam) return null;

  return (
    <>
      <ActionBar
        state={state}
        disabled={isProcessing}
        onSetDate={() => setIsScheduleModalOpen(true)}
        onChangeDate={() => setIsScheduleModalOpen(true)}
        onForfeit={() => setIsWalkoverModalOpen(true)}
        onEnterResult={() => setIsResultModalOpen(true)}
        onDeclineBraveheart={handleDeclineBraveheart}
      />

      <MatchScheduleModal
        isOpen={isScheduleModalOpen}
        tournamentId={tournamentId}
        onClose={() => setIsScheduleModalOpen(false)}
        onSuccess={handleModalSuccess}
        matchId={activeMatch.id}
        team1Name={teamNames.team1}
        team2Name={teamNames.team2}
        submittingTeamId={userTeam.id}
        mode={isResultAction ? "reschedule" : "schedule"}
        challengeExpiresAt={activeMatch.challengeExpiresAt}
        initialStreaming={
          activeMatch.streaming?.status === "scheduled"
            ? { courtNumber: activeMatch.streaming.courtNumber }
            : undefined
        }
        initialDate={activeMatch.matchDate}
        initialLocationId={activeMatch.locationId}
      />

      <MatchResultModal
        isOpen={isResultModalOpen}
        onClose={() => setIsResultModalOpen(false)}
        onSuccess={handleModalSuccess}
        matchId={activeMatch.id}
        sport={activeMatch.sport}
        team1Name={teamNames.team1}
        team2Name={teamNames.team2}
        team1Id={activeMatch.team1Id}
        team2Id={activeMatch.team2Id}
        submittingTeamId={userTeam.id}
      />

      <WalkoverConfirmModal
        isOpen={isWalkoverModalOpen}
        onClose={() => setIsWalkoverModalOpen(false)}
        onSuccess={handleModalSuccess}
        matchId={activeMatch.id}
        forfeitingTeamId={userTeam.id}
        opponentName={teamNames.opponent}
      />
    </>
  );
}
